// components/TextImageSwitcher/TextImageSwitcher.tsx
import React, { useEffect, useState, useRef } from 'react'
import ClassName from 'clsx'
import { SolutionMedicalProps } from '@/assets/content/type'
import backgroundImage from './advantage-back.png'
import styles from './style.module.scss'
import { Flex } from 'antd'

export interface TextImageSwitcherProps {
  items: SolutionMedicalProps['advantages']['items']
  image: string
  autoPlay?: boolean
  interval?: number
  className?: string
}

const TextImageSwitcher: React.FC<TextImageSwitcherProps> = ({
  items,
  autoPlay = true,
  interval = 3000,
  className,
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  // 自动轮播
  useEffect(() => {
    if (!autoPlay) return

    timerRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % items.length)
    }, interval)

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [autoPlay, interval, items.length])

  // 手动切换
  const handleSelect = (idx: number) => {
    if (idx === activeIndex) return
    setActiveIndex(idx)
    if (autoPlay && timerRef.current) {
      clearInterval(timerRef.current)
    }
  }

  return (
    <div
      className={ClassName(styles.wrapper, className)}
      style={{
        background: `url(${backgroundImage}) no-repeat center/contain`,
      }}
    >
      {/* 左侧文字列表 */}
      <ul className={styles.textList}>
        {items.map((item, idx) => (
          <li
            key={idx}
            className={ClassName(styles.textItem, {
              [styles.activeText]: idx === activeIndex,
            })}
            onClick={() => handleSelect(idx)}
          >
            <div className={styles.index}>{idx < 9 ? `0${idx + 1}` : idx + 1}</div>
            <Flex vertical>
              <div className={styles.title}>{item.title}</div>
              {idx === activeIndex && (
                <div className={ClassName(styles.descriptions, item?.descriptions?.length > 1 && styles.multiLine)}>
                  {item.descriptions.map((description, idx) => (
                    <Flex align='flex-start' key={idx}>
                      <i className={styles.dot} />
                      <span>{description}</span>
                    </Flex>
                  ))}
                </div>
              )}
            </Flex>
          </li>
        ))}
      </ul>

      {/* 右侧图片区域 */}
      <div className={styles.imgContainer}>
        {items.map((item, idx) => (
          <img
            key={idx}
            src={item.image}
            alt={item.title}
            className={ClassName(styles.img, {
              [styles.show]: idx === activeIndex,
              [styles.hide]: idx !== activeIndex,
            })}
          />
        ))}
      </div>
    </div>
  )
}

export default TextImageSwitcher
